/**
 * BusinessObjectiveForm Component
 *
 * Reusable form for creating and editing business objectives
 */

import {
  TextField,
  SelectField,
  EntityAutocompleteField,
  type CreateBusinessObjectiveFormData,
} from '../forms';
import { createBusinessObjectiveSchema } from '../forms/validation';
import { EntityForm } from '../shared';
import { TagPicker } from '../tags/TagPicker';
import { useAllStakeholders, useBusinessUnits } from '../../graphql/queries';
import '@tylertech/forge/card';
import '@tylertech/forge/icon';
import './BusinessObjectiveForm.css';

export interface BusinessObjectiveFormProps {
  /** Initial form values (for edit mode) */
  defaultValues?: Partial<CreateBusinessObjectiveFormData>;
  /** Submit handler */
  onSubmit: (data: CreateBusinessObjectiveFormData) => void | Promise<void>;
  /** Cancel handler */
  onCancel: () => void;
  /** Is form submitting */
  isSubmitting?: boolean;
  /** Form mode */
  mode: 'create' | 'edit';
}

const CardElement = 'forge-card' as React.ElementType;

/**
 * BusinessObjective create/edit form with validation
 *
 * @example
 * ```tsx
 * <BusinessObjectiveForm
 *   mode="create"
 *   onSubmit={handleCreate}
 *   onCancel={() => navigate('/business-objectives')}
 * />
 * ```
 */
export function BusinessObjectiveForm({
  defaultValues,
  onSubmit,
  onCancel,
  isSubmitting = false,
  mode,
}: BusinessObjectiveFormProps): JSX.Element {
  // Owner/Sponsor are User IDs (meta-model-spec.md) but the admin-gated REST
  // `/users` endpoint isn't readable by BusinessEditor, so both source from
  // the Stakeholder catalog instead — same pattern as BusinessProcessForm's
  // Process Owner picker (issue #769).
  const { data: stakeholdersData } = useAllStakeholders();
  const stakeholderOptions = (stakeholdersData?.stakeholders?.edges ?? []).map(({ node }) => ({
    value: node.entityId,
    label: node.name,
  }));

  const { data: businessUnitsData } = useBusinessUnits();
  const businessUnitOptions = (businessUnitsData?.businessUnits?.edges ?? []).map(({ node }) => ({
    value: node.entityId,
    label: node.name,
  }));

  return (
    <EntityForm<CreateBusinessObjectiveFormData>
      schema={createBusinessObjectiveSchema}
      baseValues={{
        objectiveType: 'strategic',
        priority: 'medium',
        status: 'draft',
        timeHorizon: 'short-term',
        tags: [],
      }}
      defaultValues={defaultValues}
      onSubmit={onSubmit}
      onCancel={onCancel}
      isSubmitting={isSubmitting}
      mode={mode}
      createLabel="Create Business Objective"
      formClassName="business-objective-form"
    >
      {({ register, formState: { errors }, values, setValue }) => {
        const tags = values.tags || [];

        return (
          <>
            <CardElement className="form-card">
              <div className="form-section">
                <h2 className="forge-typography--headline6">Basic Information</h2>

                <TextField
                  label="Name"
                  {...register('name')}
                  value={values.name || ''}
                  error={errors.name}
                  required
                  placeholder="e.g., Increase Customer Satisfaction"
                  helperText="A descriptive name for the business objective"
                />

                <TextField
                  label="Description"
                  {...register('description')}
                  value={values.description || ''}
                  error={errors.description}
                  placeholder="Brief description of the business objective"
                  helperText="Optional description (max 2000 characters)"
                />
              </div>
            </CardElement>

            <CardElement className="form-card">
              <div className="form-section">
                <h2 className="forge-typography--headline6">Classification</h2>

                <SelectField
                  label="Objective Type"
                  {...register('objectiveType')}
                  value={values.objectiveType}
                  options={[
                    { value: 'strategic', label: 'Strategic' },
                    { value: 'operational', label: 'Operational' },
                    { value: 'financial', label: 'Financial' },
                    { value: 'customer', label: 'Customer' },
                    { value: 'innovation', label: 'Innovation' },
                    { value: 'compliance', label: 'Compliance' },
                  ]}
                  error={errors.objectiveType}
                  required
                />

                <SelectField
                  label="Priority"
                  {...register('priority')}
                  value={values.priority || ''}
                  options={[
                    { value: 'critical', label: 'Critical' },
                    { value: 'high', label: 'High' },
                    { value: 'medium', label: 'Medium' },
                    { value: 'low', label: 'Low' },
                  ]}
                  error={errors.priority}
                />

                <SelectField
                  label="Status"
                  {...register('status')}
                  value={values.status || ''}
                  options={[
                    { value: 'draft', label: 'Draft' },
                    { value: 'approved', label: 'Approved' },
                    { value: 'in-progress', label: 'In Progress' },
                    { value: 'achieved', label: 'Achieved' },
                    { value: 'missed', label: 'Missed' },
                    { value: 'cancelled', label: 'Cancelled' },
                  ]}
                  error={errors.status}
                />
              </div>
            </CardElement>

            <CardElement className="form-card">
              <div className="form-section">
                <h2 className="forge-typography--headline6">Timeline</h2>

                <TextField
                  label="Start Date"
                  type="date"
                  {...register('startDate')}
                  value={values.startDate || ''}
                  error={errors.startDate}
                  helperText="Optional start date"
                />

                <TextField
                  label="Target Date"
                  type="date"
                  {...register('targetDate')}
                  value={values.targetDate || ''}
                  error={errors.targetDate}
                  helperText="Optional target completion date"
                />

                <SelectField
                  label="Time Horizon"
                  {...register('timeHorizon')}
                  value={values.timeHorizon || ''}
                  options={[
                    { value: 'short-term', label: 'Short Term' },
                    { value: 'medium-term', label: 'Medium Term' },
                    { value: 'long-term', label: 'Long Term' },
                  ]}
                  error={errors.timeHorizon}
                />
              </div>
            </CardElement>

            <CardElement className="form-card">
              <div className="form-section">
                <h2 className="forge-typography--headline6">Ownership</h2>

                <EntityAutocompleteField
                  name="ownerId"
                  label="Owner"
                  options={stakeholderOptions}
                  value={values.ownerId || ''}
                  onChange={(entityId) => setValue('ownerId', entityId, { shouldValidate: true })}
                  error={errors.ownerId}
                  required
                  placeholder="Search stakeholders…"
                  helperText="The stakeholder responsible for this objective"
                />

                <EntityAutocompleteField
                  name="owningBusinessUnitId"
                  label="Owning Business Unit"
                  options={businessUnitOptions}
                  value={values.owningBusinessUnitId || ''}
                  onChange={(entityId) =>
                    setValue('owningBusinessUnitId', entityId, { shouldValidate: true })
                  }
                  error={errors.owningBusinessUnitId}
                  placeholder="Search business units…"
                  helperText="Optional owning business unit"
                />

                <EntityAutocompleteField
                  name="sponsorId"
                  label="Sponsor"
                  options={stakeholderOptions}
                  value={values.sponsorId || ''}
                  onChange={(entityId) => setValue('sponsorId', entityId, { shouldValidate: true })}
                  error={errors.sponsorId}
                  placeholder="Search stakeholders…"
                  helperText="Optional executive sponsor"
                />
              </div>
            </CardElement>

            <CardElement className="form-card">
              <div className="form-section">
                <h2 className="forge-typography--headline6">Goals</h2>

                <TextField
                  label="Success Criteria"
                  {...register('successCriteria')}
                  value={values.successCriteria || ''}
                  error={errors.successCriteria}
                  placeholder="Describe the criteria for success"
                  helperText="Optional success criteria (max 2000 characters)"
                />

                <TextField
                  label="KPI Statement"
                  {...register('kpiStatement')}
                  value={values.kpiStatement || ''}
                  error={errors.kpiStatement}
                  placeholder="Describe the quantified KPI/metric for this objective"
                  helperText="Optional quantified KPI statement, distinct from the qualitative goal above (max 2000 characters)"
                />
              </div>
            </CardElement>

            <CardElement className="form-card">
              <div className="form-section">
                <h2 className="forge-typography--headline6">Additional Information</h2>

                <TagPicker
                  id="business-objective-tag-picker"
                  label="Tags"
                  selectedTagIds={tags}
                  onChange={(newIds) => setValue('tags', newIds)}
                />

                <TextField
                  label="Notes"
                  {...register('notes')}
                  value={values.notes || ''}
                  error={errors.notes}
                  placeholder="Additional notes about this business objective"
                  helperText="Optional notes (max 5000 characters)"
                />
              </div>
            </CardElement>
          </>
        );
      }}
    </EntityForm>
  );
}
